<template>
    <div class="text-center w-100">
        <v-snackbar width="300" multi-line top right :timeout="-1" v-model="showSnackbar">
            <div class="d-flex flex-column w-100">
                <span v-if="operationType === operationTypes.MOVE_OPERATION" class="font-weight-bold text-uppercase ml-1">file move</span>
                <span v-else-if="operationType === operationTypes.COPY_OPERATION" class="font-weight-bold text-uppercase ml-1">file copy</span>
                <v-divider color="white"></v-divider>
                <div class="mt-1 d-flex align-center justify-space-between">
                    <span>
                        <span v-if="operationType === operationTypes.MOVE_OPERATION" class="font-weight-bold secondary--text">Items to move</span>
                        <span v-else-if="operationType === operationTypes.COPY_OPERATION" class="font-weight-bold secondary--text">Items to copy</span>
                        : {{ selectedFiles.length }} selected
                    </span>
                </div>
                <div class="d-flex justify-start flex-column" v-if="filesToDisplay.length">
                    <ul>
                        <li v-for="file in filesToDisplay" :key="file.fid">
                            <div class="d-flex align-center justify-space-between">
                                <div>
                                    <span class="mr-3">{{ file.short_id }}</span>
                                    <v-btn
                                        :disabled="copyingOrMovingFiles"
                                        @click="$emit('removeFileFromMoveCopyList', { fid: file.fid })"
                                        class="ma-0 pa-0"
                                        icon
                                        color="white">
                                        <v-icon>clear</v-icon>
                                    </v-btn>
                                </div>
                                <v-tooltip v-if="alreadyExistingfiles.includes(file.fid)" left>
                                    <template v-slot:activator="{ on }">
                                        <v-icon color="error" v-on="on">info</v-icon>
                                    </template>
                                    <span>File already exists in the target directory</span>
                                </v-tooltip>
                            </div>
                        </li>
                    </ul>
                    <a v-if="selectedFiles.length > 3 && !expandFileList" @click="expandFileList = true" class="secondary--text caption ml-3">show all</a>
                    <a v-else-if="selectedFiles.length > 3 && expandFileList" @click="expandFileList = false" class="secondary--text caption ml-3">show less</a>
                </div>
                <div class="d-flex justify-start flex-column">
                    <span>
                        <span v-if="operationType === operationTypes.MOVE_OPERATION" class="font-weight-bold secondary--text">Move to</span>
                        <span v-else-if="operationType === operationTypes.COPY_OPERATION" class="font-weight-bold secondary--text">Copy to</span>
                        :
                        <v-icon color="white" small>folder</v-icon>
                        {{ $store.state.snapshotStore.pathArray.join('/') }}
                    </span>
                    <span class="caption white--text font-italic">
                        <sup>*</sup>
                        Navigate within the file browser to select the desired target location.
                    </span>
                </div>
                <div class="d-flex justify-end mt-1">
                    <v-btn :disabled="copyingOrMovingFiles" color="white" class="white--text ma-0" small @click="clearFiles" text>cancel</v-btn>
                    <v-btn
                        :disabled="!selectedFiles.length"
                        :loading="copyingOrMovingFiles"
                        color="white"
                        class="white--text ml-1"
                        small
                        outlined
                        @click="moveSelectedFiles()"
                        v-if="operationType === operationTypes.MOVE_OPERATION">
                        move
                    </v-btn>
                    <v-btn
                        :disabled="!selectedFiles.length"
                        :loading="copyingOrMovingFiles"
                        color="white"
                        class="white--text ml-1"
                        small
                        outlined
                        @click="copySelectedFiles()"
                        v-if="operationType === operationTypes.COPY_OPERATION">
                        copy
                    </v-btn>
                </div>
            </div>
        </v-snackbar>
    </div>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as filesStorageErrors from '@/modules/files/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as snapshotErrors from '@/modules/snapshot/errors/snapshots'

const { getBackendErrorMessage } = createBackendErrorHandler(filesStorageErrors, lifecycleErrors, permissionsErrors, snapshotErrors)

export default {
    name: 'FileMoveAndCopy',
    mixins: [enumsData],
    props: {
        selectedFiles: Array,
        operationType: String,
        folderTypeSelected: String
    },
    data() {
        return {
            showSnackbar: false,
            expandFileList: false,
            copyOfSelectedFiles: null,
            copyingOrMovingFiles: false,
            alreadyExistingfiles: [],
            operationTypes: {
                COPY_OPERATION: 'copy',
                MOVE_OPERATION: 'move'
            }
        }
    },
    methods: {
        clearFiles() {
            this.alreadyExistingfiles = []
            this.$emit('clearCopyAndMoveFiles')
        },
        moveSelectedFiles: function () {
            if (this.selectedFiles.length) {
                this.copyingOrMovingFiles = true
                let patchBody
                if (this.folderTypeSelected === this.folderTypesLabels.PERSONAL_FILES) {
                    patchBody = {
                        local_path: `${this.$store.state.userInfo.uid}${this.$store.state.snapshotStore.pathArray.join('/')}`,
                        area: this.folderTypesLabels.PERSONAL_FILES
                    }
                } else {
                    patchBody = { local_path: this.$store.state.snapshotStore.pathArray.join('/'), area: this.folderTypesLabels.WORKSPACE_FILES }
                }
                let numFilesToMove = this.selectedFiles.length
                let numFilesMovedSuccessfully = 0
                const errors = []
                this.alreadyExistingfiles = []
                const existingFiles = []
                this.copyOfSelectedFiles.forEach(file => {
                    this.$axios
                        .patch(`/files/${file.fid}`, patchBody, { timeout: 30000 })
                        .then(response => {
                            numFilesMovedSuccessfully += 1
                            this.$store.dispatch('showSnackBar', {
                                snackBarText: `Moving files, please wait.....`,
                                snackBarTimeout: 15000,
                                snackBarIcon: 'info'
                            })
                            this.copyOfSelectedFiles = this.copyOfSelectedFiles.filter(singleFile => singleFile.fid !== file.fid)
                            this.$emit('removeFileFromMoveCopyList', { fid: file.fid })
                        })
                        .catch(error => {
                            errors.push(error)
                            if (error.response?.data?.reason?.includes('already exists')) {
                                existingFiles.push(file.fid)
                            }
                        })
                        .finally(() => {
                            numFilesToMove -= 1
                            if (numFilesToMove === 0) {
                                if (!this.copyOfSelectedFiles.length) {
                                    this.$store.dispatch('showSnackBar', {
                                        snackBarText: `File(s) successfully moved!`,
                                        snackBarTimeout: 5000,
                                        snackBarIcon: 'check_circle'
                                    })
                                } else {
                                    const messages = errors
                                        .map(error => getBackendErrorMessage(error, 'Failed to move the selected files. Please try again.'))
                                        .filter(Boolean)
                                    if (messages.length) {
                                        this.$store.dispatch('showSnackBar', {
                                            snackBarText: [...new Set(messages)].join(' '),
                                            snackBarTimeout: 15000,
                                            snackBarIcon: 'error'
                                        })
                                    }
                                }
                                if (numFilesMovedSuccessfully > 0) {
                                    this.$store.dispatch('snapshotStore/fetchCurrentFiles', {
                                        id: this.$route.params.snid,
                                        route: this.$route,
                                        setFetchingStatus: true
                                    })
                                }
                                if (existingFiles.length) {
                                    this.alreadyExistingfiles = existingFiles
                                }
                                this.copyingOrMovingFiles = false
                            }
                        })
                })
            }
        },
        copySelectedFiles: function () {
            if (this.selectedFiles.length) {
                this.copyingOrMovingFiles = true
                let apiURL
                let numFilesToCopy = this.selectedFiles.length
                let numFilesCopiedSuccessfully = 0
                const errors = []
                const userTasksRoute = this.$router.resolve({ name: 'user-tasks' }).href
                const successMessage = `Files successfully submitted for copying, you can track progress <a href="${userTasksRoute}">here</a>`
                this.copyOfSelectedFiles.forEach(file => {
                    let postBody
                    if (this.folderTypeSelected === this.folderTypesLabels.PERSONAL_FILES) {
                        postBody = {
                            local_path: `${this.$store.state.userInfo.uid}${this.$store.state.snapshotStore.pathArray.join('/')}`,
                            area: this.folderTypesLabels.PERSONAL_FILES
                        }
                    } else {
                        postBody = { local_path: this.$store.state.snapshotStore.pathArray.join('/'), area: 'files' }
                    }
                    apiURL = `/files/${file.fid}/duplicate_async`
                    this.$axios
                        .post(apiURL, postBody)
                        .then(response => {
                            numFilesCopiedSuccessfully += 1
                            this.$store.dispatch('showSnackBar', {
                                snackBarText: `Copying file ${file.short_id}`,
                                snackBarTimeout: 15000,
                                snackBarIcon: 'info'
                            })
                            this.copyOfSelectedFiles = this.copyOfSelectedFiles.filter(singleFile => singleFile.fid !== file.fid)
                            this.$emit('removeFileFromMoveCopyList', { fid: file.fid })
                        })
                        .catch(error => {
                            errors.push(error)
                        })
                        .finally(() => {
                            numFilesToCopy -= 1
                            if (numFilesToCopy === 0) {
                                if (!this.copyOfSelectedFiles.length) {
                                    this.$store.dispatch('showSnackBar', {
                                        snackBarText: successMessage,
                                        snackBarTimeout: 10000,
                                        snackBarIcon: 'check_circle'
                                    })
                                } else {
                                    const messages = errors
                                        .map(error => getBackendErrorMessage(error, 'Failed to copy the selected files. Please try again.'))
                                        .filter(Boolean)
                                    if (messages.length) {
                                        this.$store.dispatch('showSnackBar', {
                                            snackBarText: [...new Set(messages)].join(' '),
                                            snackBarTimeout: 15000,
                                            snackBarIcon: 'error'
                                        })
                                    }
                                }
                                if (numFilesCopiedSuccessfully > 0) {
                                    this.$store.dispatch('snapshotStore/fetchCurrentFiles', {
                                        id: this.$route.params.snid,
                                        route: this.$route,
                                        setFetchingStatus: true
                                    })
                                }
                                this.$store.dispatch('userStore/fetchUserTasks')
                                this.copyingOrMovingFiles = false
                            }
                        })
                })
            }
        }
    },
    computed: {
        filesToDisplay() {
            if (this.copyOfSelectedFiles && !this.expandFileList) {
                return this.copyOfSelectedFiles.slice(0, 3)
            } else if (this.copyOfSelectedFiles) {
                return this.copyOfSelectedFiles
            }
            return []
        }
    },
    watch: {
        copyOfSelectedFiles: function (nextVal) {
            if (nextVal && nextVal.length && !this.showSnackbar) {
                this.showSnackbar = true
            } else if (!nextVal.length && this.showSnackbar) {
                this.showSnackbar = false
            }
        },
        selectedFiles: function (nextVal) {
            this.copyOfSelectedFiles = nextVal
            this.alreadyExistingfiles = []
        },
        operationType: function (nextVal) {
            if (nextVal === this.operationTypes.COPY_OPERATION) {
                this.alreadyExistingfiles = []
            }
        }
    }
}
</script>
